import { useEffect, useMemo, useCallback, useState } from "react";
import { CkycataDtoV1 } from "../../dto/ckyc.dto";
import { EKYCUserDetails, FileType } from "../../models";
import DataDisplaySection from "../datadisplay-section";
import { DatafieldLabel } from "../datafield-section";
import "./style.css";

interface JsonDataViewerProps {
  docUrl: string;
  isCkyc: boolean;
}

const EKYCUserDetailsLabels: DatafieldLabel[] = [
  {
    items: [
      { label: "Name:", key: "name" },
      { label: "Date of Birth:", key: "dob" },
      { label: "Masked Aadhaar:", key: "maskedAadhaar" },
      { label: "Care of:", key: ["address.careof", "address.co"] },
      {
        label: "House:",
        key: "address.house",
      },
      { label: "Street:", key: "address.street" },
      { label: "Landmark:", key: "address.landmark" },
    ],
  },
  {
    items: [
      { label: "Locality:", key: "address.loc" },
      { label: "Sub District:", key: "address.subdist" },
      { label: "District:", key: "address.dist" },
      { label: "City:", key: "address.vtc" },
      { label: "State:", key: "address.state" },
      { label: "Country:", key: "address.country" },
      { label: "Pincode:", key: "address.pc" },
    ],
  },
];

const CkycUserDetailsLabels: DatafieldLabel[] = [
  {
    items: [
      { label: "CKYC ID", key: "ckycId" },
      { label: "Full Name", key: "fullName" },
      { label: "Parent / Spouse Name:", key: "parentOrSpouseName" },
      { label: "Email", key: "email" },
      { label: "Mobile", key: "mobile" },
      { label: "PAN", key: "pan" },
    ],
  },
  {
    items: [
      { label: "DOB", key: "dob" },
      { label: "Gender", key: "gender" },
      { label: "House Info", key: "permanentAddress.houseInfo" },
      { label: "Locality Info", key: "permanentAddress.localityInfo" },
      { label: "City", key: "permanentAddress.city" },
      { label: "State", key: "permanentAddress.state" },
      { label: "Pincode", key: "permanentAddress.pinCode" },
    ],
  },
];

function isCkycUserDetails(docUrl: string) {
  const jsonString = decodeURIComponent(docUrl.split(",")[1]);
  const parsedData = JSON.parse(jsonString);
  return !!parsedData?.ckycId;
}

export function JsonDataViewer({ docUrl, isCkyc }: JsonDataViewerProps) {
  const parsedData = useMemo(() => {
    try {
      if (!docUrl) return null;
      const [, base64Part] = docUrl.split(",");
      const jsonString = decodeURIComponent(base64Part);
      return JSON.parse(jsonString);
    } catch (error) {
      console.error("Failed to parse user details from docUrl:", error);
      return null;
    }
  }, [docUrl]);

  const data = parsedData as EKYCUserDetails | CkycataDtoV1 | null;
  const labels = isCkyc ? CkycUserDetailsLabels : EKYCUserDetailsLabels;
  const footerLabel = isCkyc
    ? "Details fetched from CKYC records"
    : "Aadhaar details as per EKYC XML file";

  if (!data) {
    return <div className="rf-doc-viewer__fallback-text">Loading...</div>;
  }

  return (
    <div className="rf-doc-viewer__doc">
      <div className="dashboard-container">
        <DataDisplaySection data={data} labels={labels} />
        <div className="d-flex align-items-center justify-content-end mt-3">
          <p style={{ color: "var(--primary)" }}>
            <b>{footerLabel}</b>
          </p>
        </div>
      </div>
    </div>
  );
}

function dataUriToBlobUrl(dataUri: string): string | null {
  const commaIndex = dataUri.indexOf(",");
  if (commaIndex === -1) return null;
  const base64 = dataUri.slice(commaIndex + 1);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return URL.createObjectURL(new Blob([bytes], { type: FileType.APP_PDF }));
}

function PdfObjectViewer({ docUrl }: { docUrl: string }) {
  const isDataUri = docUrl.startsWith("data:");
  const [blobUrl, setBlobUrl] = useState<string | null>(
    isDataUri ? null : docUrl,
  );

  useEffect(() => {
    if (!isDataUri) {
      setBlobUrl(docUrl);
      return;
    }
    const created = dataUriToBlobUrl(docUrl);
    setBlobUrl(created ?? docUrl);
    return () => {
      if (created) URL.revokeObjectURL(created);
    };
  }, [docUrl, isDataUri]);

  if (!blobUrl) {
    return (
      <div className="rf-doc-viewer__doc embedded-view">
        <p className="rf-doc-viewer__fallback-text">Loading...</p>
      </div>
    );
  }

  return (
    <div className="rf-doc-viewer__doc embedded-view">
      <object type={FileType.APP_PDF} data={blobUrl} width="100%" height="100%">
        <p className="rf-doc-viewer__fallback-text">
          Your browser cannot display the file.{" "}
          <a href={docUrl} download>
            Click here
          </a>{" "}
          to download it
        </p>
      </object>
    </div>
  );
}

function renderDocViewer(docType: FileType | undefined, docUrl?: string) {
  if (!docUrl) {
    return (
      <div className="rf-doc-viewer__doc">
        <p className="rf-doc-viewer__fallback-text">Preview not available</p>
      </div>
    );
  }

  if (docType === FileType.APP_JSON) {
    const isCkyc = isCkycUserDetails(docUrl);
    return <JsonDataViewer docUrl={docUrl} isCkyc={isCkyc} />;
  }

  if (docType === FileType.TEXT_JSON) {
    return (
      <div className="rf-doc-viewer__doc embedded-view">
        <iframe src={docUrl} frameBorder="0" />
      </div>
    );
  }
  if (docType === FileType.IMG_JPEG || docType === FileType.IMG_PNG) {
    return (
      <figure className="rf-doc-viewer__doc">
        <img src={docUrl} alt="document" />
      </figure>
    );
  }
  if (docType === FileType.APP_PDF) {
    return <PdfObjectViewer docUrl={docUrl} />;
  }
  return (
    <div className="rf-doc-viewer__doc">
      <p className="rf-doc-viewer__fallback-text">Preview not available</p>
    </div>
  );
}

interface DocumentViewerProps {
  active: boolean;
  docUrl?: string;
  docType?: FileType;
  onClose?: () => void;
}

export default function DocumentViewer({
  active = false,
  docUrl,
  docType,
  onClose,
}: DocumentViewerProps) {
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const hanldeKeyPress = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      handleClose();
    }
  }, [handleClose]);

  useEffect(() => {
    window.document.addEventListener("keyup", hanldeKeyPress);
    return () => {
      window.document.removeEventListener("keyup", hanldeKeyPress);
    };
  }, [hanldeKeyPress]);

  return (
    <div
      className="rf-doc-viewer"
      style={active ? { display: "flex" } : undefined}
    >
      <span
        className="rf-doc-viewer__close"
        onClick={handleClose}
        role="button"
      >
        &times;
      </span>
      {renderDocViewer(docType, docUrl)}
    </div>
  );
}
